import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MyAccountContainer } from '@components/organisms/MyAccountContainer';
import { ServiceAboutText } from '@components/molecules/ServiceAboutText';
import { ServiceGallery } from '@components/molecules/ServiceGallery';
import { Space } from '@components/atoms/Space';
import { ServiceProfile, ServiceProfileProps } from '@components/molecules/ServiceProfile';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ProductsHighlightsSlider } from '@components/molecules/ProductsHighlightsSlider';
import { ProductCardProps } from '@components/molecules/ProductCard';
import { EditCompanyAddressContactsAndScheduleModal } from './modals/EditCompanyAddressContactsAndSchedule';
import { EditCompanyModal } from './modals/EditCompanyModal';
import { EditGalleryModal } from './modals/EditGalleryModal';
import { useData } from '@hooks/useData';
import { ServicesGrid, ServicesGridProps } from '@components/molecules/ServicesGrid';

export enum CompanyEditBlock {
    banner,
    logo,
    nameAndSocialLinks,
    aboutText,
}

export interface MyCompanyProps {
    uuid: string;
    name: string;
    logo?: ServiceProfileProps['logo'];
    email: string;
    phoneNumber: string;
    address: string[];
    locations?: {
        uuid: string;
        latitude: number;
        longitude: number;
    }[];
    schedule: { header?: string; text: string }[];
    bannerImage?: ServiceProfileProps['bannerImage'];
    website: string;
    type: string;
    aboutText: string;
    images: {
        src?: string;
        visible?: boolean;
    }[];
    socialLinks: ServiceProfileProps['socialLinks'];
    services: ServicesGridProps['services'];
    productHighlights: ProductCardProps[];
}

export const MyCompany: FC<MyCompanyProps> = ({
    uuid,
    name,
    logo,
    email,
    phoneNumber,
    address,
    locations,
    schedule,
    bannerImage,
    website,
    type,
    aboutText,
    images,
    socialLinks,
    services,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    productHighlights,
}) => {
    const { setMyAccountStoreDetail } = useData();
    const { t } = useTranslation();

    const translations = {
        title: t('my-company-component.title'),
        galleryAlt: (index: number) => t('my-company-component.gallery.alt', { index: `${index}`, name: `${name}` }),
        productHighlightsTitle: t('my-company-component.product-highlights.title'),
        productHighlightsActionLabel: t('my-company-component.product-highlights.action.label'),
        servicesTypeLabel: t('merchant-detail-component.services-label'),
        productsTypeLabel: t('merchant-detail-component.products-label'),
    };

    useEffect(() => {
        // when unmounting page, reset results
        // to prevent showing old results when returning to this page
        return () => {
            setMyAccountStoreDetail(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [editModal, setEditModal] = useState<CompanyEditBlock | null>(null);
    const [showAddressContactsAndScheduleModal, setShowAddressContactsAndScheduleModal] = useState<boolean>(false);
    const [showGalleryModal, setShowGalleryModal] = useState<boolean>(false);

    return (
        <MyAccountContainer title={translations.title}>
            <Space spaceBreakpoints={{ xs: { space: 30 } }} />

            <ServiceProfile
                name={name}
                merchantName={name}
                logo={logo}
                email={email}
                phoneNumber={phoneNumber}
                address={address}
                locations={locations}
                schedule={schedule}
                bannerImage={bannerImage}
                link={
                    type === 'service'
                        ? { label: translations.servicesTypeLabel, url: website }
                        : { label: translations.productsTypeLabel, url: 'products?stores=' + uuid }
                }
                socialLinks={socialLinks}
                onEditClickBanner={() => setEditModal(CompanyEditBlock.banner)}
                onEditClickLogo={() => setEditModal(CompanyEditBlock.logo)}
                onEditClickNameCaesUrlAndSocials={() => setEditModal(CompanyEditBlock.nameAndSocialLinks)}
                onEditClickAddressAndContacts={() => setShowAddressContactsAndScheduleModal(true)}
            />

            <Space spaceBreakpoints={{ xs: { space: 50 }, md: { space: 100 } }} />

            <ServiceAboutText content={aboutText} onEditClick={() => setEditModal(CompanyEditBlock.aboutText)} />

            <>
                {services.length > 0 && (
                    <>
                        <Space spaceBreakpoints={{ xs: { space: 50 }, md: { space: 100 } }} />
                        <ServicesGrid services={services} merchantName={name} />
                    </>
                )}
            </>

            <Space spaceBreakpoints={{ xs: { space: 50 }, md: { space: 100 } }} />

            <ServiceGallery
                images={images
                    .filter((image) => image.visible === true)
                    .map((image, index) => {
                        return { src: image.src, alt: translations.galleryAlt(index + 1) };
                    })}
                onEditClick={() => setShowGalleryModal(true)}
            />

            {/* #TODO: for now: products highlights are random products
            <Space spaceBreakpoints={{ xs: { space: 50 }, md: { space: 100 } }} />

            <ProductsHighlightsSlider
                title={translations.productHighlightsTitle}
                actionLabel={translations.productHighlightsActionLabel}
                linkTo="products"
                products={productHighlights}
                editMode={true}
            />*/}

            <EditCompanyModal type={type} block={editModal} onClose={() => setEditModal(null)} />

            <EditCompanyAddressContactsAndScheduleModal
                show={showAddressContactsAndScheduleModal}
                onClose={() => setShowAddressContactsAndScheduleModal(false)}
            />
            <EditGalleryModal show={showGalleryModal} onClose={() => setShowGalleryModal(false)} />
        </MyAccountContainer>
    );
};
